/**
 * URLLinkBuilder - Centralized URL building utility for Medentic application
 *
 * Uses environment variables:
 * - VITE_SITE_URL: Base URL for the main website (www.medentic.app)
 * - VITE_SUPPORT_URL: Base URL for the support/help center (support.medentic.app)
 * - VITE_CHECKOUT_URL: Base URL for the checkout service
 */
export class URLLinkBuilder {
  private static siteUrl = import.meta.env.VITE_SITE_URL;
  private static supportUrl = import.meta.env.VITE_SUPPORT_URL;
  private static checkoutUrl = import.meta.env.VITE_CHECKOUT_URL;

  /**
   * Helper to build URL with optional language prefix
   */
  private static buildSiteUrl(path: string, language?: string): string {
    const langPrefix = language ? `/${language}` : "";
    return `${this.siteUrl}${langPrefix}${path}`;
  }

  // ========================================
  // Website URLs (www.medentic.app)
  // ========================================

  /**
   * Downloads page URL
   * @param language - Optional language code (e.g., 'en', 'bg')
   * @returns Full URL to downloads page
   * @example
   * wwwDownloads() → https://www.medentic.app/downloads
   * wwwDownloads('en') → https://www.medentic.app/en/downloads
   */
  static wwwDownloads(language?: string): string {
    return this.buildSiteUrl("/downloads", language);
  }

  /**
   * Legal pages URL (terms of service, privacy policy)
   * @param page - Legal page type
   * @param language - Optional language code (e.g., 'en', 'bg')
   * @returns Full URL to legal page
   * @example
   * wwwLegal('privacy') → https://www.medentic.app/legal/privacy
   * wwwLegal('terms-of-service', 'bg') → https://www.medentic.app/bg/legal/terms-of-service
   */
  static wwwLegal(
    page: "terms-of-service" | "privacy",
    language?: string
  ): string {
    return this.buildSiteUrl(`/legal/${page}`, language);
  }

  /**
   * Support/Help center URL
   * @param path - Optional path within support section
   * @param language - Optional language code (e.g., 'en', 'bg')
   * @returns Full URL to support page
   */
  static wwwSupport(path?: string, language?: string): string {
    const supportPath = path ? `/support${path}` : "/support";
    return this.buildSiteUrl(supportPath, language);
  }

  /**
   * Changelog page URL
   * @returns Full URL to changelog page
   */
  static wwwChangelog(): string {
    return `${this.siteUrl}/changelog`;
  }

  /**
   * Event public page URL
   * @param slug - Event slug
   * @returns Full URL to event page
   */
  static wwwEvent(slug: string): string {
    return `${this.siteUrl}/event/${slug}`;
  }

  /**
   * Expert/Instructor public page URL
   * @param slug - Expert slug
   * @returns Full URL to expert page
   */
  static wwwExpert(slug: string): string {
    return `${this.siteUrl}/expert/${slug}`;
  }

  /**
   * Venue public page URL
   * @param slug - Venue slug
   * @returns Full URL to venue page
   */
  static wwwVenue(slug: string): string {
    return `${this.siteUrl}/venue/${slug}`;
  }

  /**
   * Brand events page URL
   * @param slug - Brand slug
   * @returns Full URL to brand events page
   */
  static wwwBrandEvents(slug: string): string {
    return `${this.siteUrl}/events/${slug}`;
  }

  /**
   * Logout page URL
   * @returns Full URL to logout page
   * @example
   * wwwLogout() → https://www.medentic.app/logout
   */
  static wwwLogout(): string {
    return `${this.siteUrl}/logout`;
  }

  /**
   * Get the slug prefix for display (without protocol)
   * @param type - Entity type: 'event' | 'expert' | 'venue' | 'brand'
   * @returns Slug prefix for display
   */
  static getSlugPrefix(type: "event" | "expert" | "venue" | "brand"): string {
    const baseWithoutProtocol = this.siteUrl.replace(/^https?:\/\//, "");
    const paths: Record<string, string> = {
      event: "/event/",
      expert: "/expert/",
      venue: "/venue/",
      brand: "/events/",
    };
    return `${baseWithoutProtocol}${paths[type]}`;
  }

  // ========================================
  // Support/Help Center URLs (support.medentic.app)
  // ========================================

  /**
   * Support help center URL
   * @param path - Optional path (e.g., '/articles/123', '/collections/456')
   * @param language - Optional language code (e.g., 'en', 'bg')
   * @returns Full URL to support help center
   */
  static supportHelpCenter(path?: string, language?: string): string {
    const langPrefix = language ? `/${language}` : "";
    const pathSuffix = path || "";
    return `${this.supportUrl}${langPrefix}${pathSuffix}`;
  }

  // ========================================
  // Checkout URLs
  // ========================================

  /**
   * Checkout ticket promo link
   * @param eventId - Event ID
   * @param ticketId - Ticket ID (used as promo code)
   * @param language - Optional language code (e.g., 'en', 'bg')
   * @returns Full checkout URL with query parameters
   */
  static checkoutTicketPromo(eventId: string, ticketId: string, language?: string): string {
    const params: Record<string, string> = {
      eventId,
      promo: ticketId,
    };
    const langPath:string = language ? `/${language}` : "";
    return `${this.checkoutUrl}${langPath}/?${new URLSearchParams(params).toString()}`;
  }

  /**
   * Checkout ticket promo link
   * @param eventId - Event ID
   * @param language - Optional language code (e.g., 'en', 'bg')
   * @returns Full checkout URL with query parameters
   */
  static checkoutEvent(eventId: string, language?: string): string {
    const params: Record<string, string> = {
      eventId,
    };
    const langPath:string = language ? `/${language}` : "";
    return `${this.checkoutUrl}${langPath}/?${new URLSearchParams(params).toString()}`;
  }
}
